import { Link } from 'wouter';
import { LegalSection } from './LegalSection';
import { routes } from '../../lib/assessment/routes';
import { REPORT_PRICE } from '../../lib/pricing';
import { usePageMeta } from '../../lib/usePageMeta';

/**
 * Terms of Service.
 *
 * A faithful migration of the legacy page at
 * `artifacts/high-performer-os/src/pages/terms.tsx`. The substantive text is
 * copied verbatim and is not rewritten, summarized, or modernized here.
 *
 * V2-018C-2 completes the migration on the same three points `/privacy`
 * needed: the page metadata, the legacy content measure, and the closing link
 * row. The service description also reads `REPORT_PRICE` again rather than
 * carrying an inlined 19.99, which is how legacy writes it.
 *
 * Rendering adaptations only: the legacy page carried its own logo header and
 * `← Back` link, which the Experience Shell header now provides.
 *
 * Launch readiness — Slice A departs from legacy on one point of fact. Legacy
 * described two assessments and two full reports. This product sells three of
 * each (`report_bottleneck`, `report_work_fit`, `report_perspective` in the
 * catalogue). The description matches what is actually offered.
 *
 * The daily check-in dashboard was struck from this list when it did not exist
 * here. It does now, so legacy's line is back: this document describes the
 * product, and the product has check-ins.
 *
 * Slice C continues that: the account clause now matches the product, which
 * lets a visitor complete an assessment signed out and requires an account to
 * view the result; and the report-credit, bundle, retake, and coupon language
 * is removed until those exist. Nothing here is a wording preference — each is
 * a statement the product could not currently honour.
 */
const EFFECTIVE_DATE = 'June 11, 2026';

export function TermsOfService() {
  usePageMeta({
    title: 'Terms of Service | High Performer OS',
    description:
      'Read the High Performer OS terms of service governing use of our three free performance assessments and optional paid full reports.',
    canonical: 'https://highperformeros.com/terms',
  });

  return (
    <article className="mx-auto max-w-3xl space-y-10 px-4 py-16">
      <div>
        <p className="mb-2 text-xs font-bold tracking-widest uppercase text-primary">
          Legal
        </p>
        <h1 className="mb-3 text-3xl font-bold">Terms of Service</h1>
        <p className="text-sm text-muted-foreground">
          Effective date: {EFFECTIVE_DATE}
        </p>
      </div>

      <div className="space-y-8">
        <LegalSection title="Agreement to Terms">
          <p>
            By accessing or using High Performer OS (the "Service"), you agree
            to be bound by these Terms of Service ("Terms"). If you do not agree
            to these Terms, do not use the Service. These Terms apply to all
            visitors, users, and others who access the Service.
          </p>
        </LegalSection>

        <LegalSection title="Description of Service">
          <p>High Performer OS is a personal development tool that provides:</p>
          <ul className="mt-2 list-inside list-disc space-y-1.5 text-sm">
            <li>
              Free 50-question assessments: the Bottleneck Assessment, the Work
              Fit Assessment, and the Perspective Assessment
            </li>
            <li>
              Free results for each assessment including your profile type and
              dimension scores
            </li>
            <li>{`Optional paid full reports ($${REPORT_PRICE} one-time payment each), delivered as downloadable PDFs. The Bottleneck Full Report includes Stop/Start behavior lists, a 7-Day Reset Journal, and a 30-Day Reset Workbook. The Work Fit Full Report includes environment dimension analysis and work environment fit tools. The Perspective Full Report includes your dominant and secondary perspective patterns, a 7-Day Perspective Reset, and a 30-Day New Mindset Practice.`}</li>
            <li>An optional daily check-in dashboard</li>
          </ul>
        </LegalSection>

        <LegalSection title="Eligibility">
          <p>
            You must be at least 18 years old to use the Service. By using the
            Service, you represent that you are 18 or older and that you have
            the legal capacity to enter into a binding agreement. If you are
            under 18, you may not use the Service.
          </p>
        </LegalSection>

        <LegalSection title="Accounts">
          <p>
            You may complete an assessment without an account. To view your
            result, and to purchase and access a full report, you must create an
            account. You are responsible for maintaining the confidentiality of
            your account credentials and for all activities that occur under
            your account. You agree to notify us immediately of any unauthorized
            use of your account.
          </p>
          <p>
            We reserve the right to terminate accounts, refuse service, or
            remove content at our discretion, including for violations of these
            Terms.
          </p>
        </LegalSection>

        <LegalSection title="Payments and Refunds">
          <h4 className="mb-1 text-sm font-semibold">Payment</h4>
          <p>
            High Performer OS full reports, downloadable materials, and related
            digital access may be available for purchase. Payment is processed
            securely by Stripe, Inc. By completing a purchase, you authorize us
            to charge the stated amount to your selected payment method.
          </p>

          <h4 className="mt-4 mb-1 text-sm font-semibold">Digital Delivery</h4>
          <p>
            High Performer OS products are digital products. Purchased reports,
            workbooks, and related digital materials may be made available
            immediately after checkout or after completion of the related
            assessment.
          </p>

          <h4 className="mt-4 mb-1 text-sm font-semibold">Refunds</h4>
          <p>
            All sales are final once digital access is delivered, except where
            required by law.
          </p>
          <p>
            Because High Performer OS provides immediate access to digital
            assessment results, personalized reports, downloadable materials,
            and online content, we do not offer refunds after delivery.
          </p>
          <p>
            If a technical issue prevents you from accessing something you
            purchased, please contact us within 7 days of purchase at{' '}
            <a
              href="mailto:support@highperformeros.com"
              className="underline transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              support@highperformeros.com
            </a>
            . We will make reasonable efforts to restore access or resolve the
            issue.
          </p>
        </LegalSection>

        <LegalSection title="Not Medical or Clinical Advice">
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
            <p className="mb-2 text-sm font-semibold text-amber-400">
              Important Disclaimer
            </p>
            <p>
              High Performer OS is a personal development and coaching tool. The
              assessment, results, report content, and all materials provided
              through the Service are for informational and self-development
              purposes only.
            </p>
            <p className="mt-2">
              The Service does not provide medical, psychological, psychiatric,
              or clinical advice, diagnosis, or treatment. Nothing in the Service
              should be construed as a substitute for professional mental health
              care, medical advice, or treatment.
            </p>
            <p className="mt-2">
              If you are experiencing a mental health crisis or require clinical
              support, please contact a licensed mental health professional or
              emergency services.
            </p>
          </div>
        </LegalSection>

        <LegalSection title="Intellectual Property">
          <p>
            All content provided through the Service, including the assessment
            questions, report content, workbook materials, design, and software,
            is owned by or licensed to High Performer OS and is protected by
            applicable intellectual property laws.
          </p>
          <p>
            You are granted a personal, non-transferable, non-exclusive license
            to access and use your purchased report for your own personal
            development. You may not reproduce, distribute, resell, sublicense,
            or publicly display any portion of the Service or its content
            without our written permission.
          </p>
        </LegalSection>

        <LegalSection title="User Conduct">
          <p>You agree not to:</p>
          <ul className="mt-2 list-inside list-disc space-y-1.5 text-sm">
            <li>
              Use the Service for any unlawful purpose or in violation of these
              Terms
            </li>
            <li>
              Attempt to gain unauthorized access to any part of the Service or
              its related systems
            </li>
            <li>
              Resell, redistribute, or commercially exploit your purchased
              report or any Service content
            </li>
            <li>
              Impersonate any person or entity or misrepresent your affiliation
              with any person or entity
            </li>
            <li>
              Interfere with or disrupt the integrity or performance of the
              Service
            </li>
          </ul>
        </LegalSection>

        <LegalSection title="Limitation of Liability">
          <p>
            To the maximum extent permitted by applicable law, High Performer OS
            and its operators shall not be liable for any indirect, incidental,
            special, consequential, or punitive damages, including loss of
            profits, data, or goodwill, arising from your use of or inability to
            use the Service.
          </p>
          <p>
            Our total liability to you for any claim arising out of or relating
            to these Terms or the Service shall not exceed the amount you paid
            us in the 12 months preceding the claim (or $39.99 if no purchase
            was made).
          </p>
        </LegalSection>

        <LegalSection title="Disclaimer of Warranties">
          <p>
            The Service is provided "as is" and "as available" without
            warranties of any kind, either express or implied, including but not
            limited to warranties of merchantability, fitness for a particular
            purpose, or non-infringement. We do not warrant that the Service
            will be uninterrupted, error-free, or free of harmful components.
          </p>
        </LegalSection>

        <LegalSection title="Governing Law">
          <p>
            These Terms are governed by and construed in accordance with the
            laws of the United States, without regard to conflict of law
            provisions. Any disputes arising under these Terms shall be resolved
            in the applicable courts of competent jurisdiction.
          </p>
        </LegalSection>

        <LegalSection title="Changes to These Terms">
          <p>
            We reserve the right to update these Terms at any time. When we do,
            we will update the effective date at the top. Continued use of the
            Service after changes are posted constitutes your acceptance of the
            revised Terms. For material changes, we will make reasonable efforts
            to provide notice.
          </p>
        </LegalSection>

        <LegalSection title="Contact">
          <p>If you have questions about these Terms, please contact us:</p>
          <div className="mt-3 rounded-lg border border-border bg-card p-4 text-sm">
            <p className="font-semibold">High Performer OS</p>
            <p className="mt-1 text-muted-foreground">
              Email:{' '}
              <a
                href="mailto:support@highperformeros.com"
                className="underline transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                support@highperformeros.com
              </a>
            </p>
          </div>
        </LegalSection>
      </div>

      {/* The legacy page's closing link row; see the note on `PrivacyPolicy`. */}
      <div
        data-testid="legal-page-links"
        className="flex gap-6 border-t border-border pt-8 text-xs text-muted-foreground"
      >
        <Link
          href={routes.privacy}
          className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          Privacy Policy
        </Link>
        <Link
          href={routes.home}
          className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          Home
        </Link>
      </div>
    </article>
  );
}
